/**
 * Network — Pure diff functions (browser-safe).
 *
 * This module re-exports just the pure parts of the diff engine so the UI
 * can use them without dragging in the registry / Redis client (which
 * imports node:os and ioredis). The CLI + gateway use `./diff.ts` instead.
 */

import type { InstanceComparison, InstanceDiff, InstanceRecord } from "./types.js";

/**
 * Diff an in-memory list of records. Browser-safe: no I/O.
 */
export function compareRecords(records: InstanceRecord[]): InstanceComparison {
  return {
    ids: records.map((r) => r.id),
    differences: diffRecords(records),
    generatedAt: Date.now(),
  };
}

/**
 * Walk a fixed set of paths and emit one InstanceDiff per path where at
 * least two values disagree. Paths are intentionally hard-coded rather than
 * recursive — the registry has a known, small shape and we want the output
 * to be deterministic.
 */
export function diffRecords(records: InstanceRecord[]): InstanceDiff[] {
  if (records.length < 2) {
    return [];
  }
  const out: InstanceDiff[] = [];

  const push = (path: string, values: Record<string, unknown>, rationale?: string) => {
    const uniq = new Set(Object.values(values).map((v) => JSON.stringify(v)));
    if (uniq.size > 1) {
      out.push({ path, values, rationale });
    }
  };

  // Top-level scalars
  push("role", index(records, "role"));
  push("version", index(records, "version"));
  push(
    "profile",
    index(records, "profile"),
    "Different profiles usually mean different on-disk state dirs and ports.",
  );
  push("hostname", index(records, "hostname"));
  push("name", index(records, "name"));

  // Redis setup — type first, then sub-fields.
  push(
    "redisSetup.type",
    index(records, (r) => r.redisSetup.type),
    "Standalone is simplest; Cluster scales horizontally; Sentinel fronts primaries for HA; 'none' means Redis is disabled locally.",
  );

  // Cluster nodes — only meaningful when there are 2+ cluster records.
  const clusterRecords = records.filter((r) => r.redisSetup.type === "cluster");
  if (clusterRecords.length >= 2) {
    const nodeLists = clusterRecords.map((r) =>
      r.redisSetup.type === "cluster" ? (r.redisSetup.nodes ?? []) : [],
    );
    const distinct = new Set(nodeLists.map((a) => JSON.stringify([...a].toSorted())));
    if (distinct.size > 1) {
      const values: Record<string, unknown> = {};
      for (const r of records) {
        values[r.id] = r.redisSetup.type === "cluster" ? (r.redisSetup.nodes ?? []) : null;
      }
      out.push({
        path: "redisSetup.cluster.nodes",
        values,
        rationale: "Cluster topology differences imply sharding / replica placement choices.",
      });
    }
  }

  // Sentinel
  const sentinelMasters = records.map((r) =>
    r.redisSetup.type === "sentinel" ? (r.redisSetup.master ?? null) : null,
  );
  if (new Set(sentinelMasters.map((v) => JSON.stringify(v))).size > 1) {
    out.push({
      path: "redisSetup.sentinel.master",
      values: Object.fromEntries(records.map((r, i) => [r.id, sentinelMasters[i]])),
      rationale: "Different sentinel master names imply different failure domains.",
    });
  }

  // Redis pool / timeouts
  push(
    "redisPool.connectTimeoutMs",
    index(records, (r) => r.redisPool.connectTimeoutMs),
  );
  push(
    "redisPool.commandTimeoutMs",
    index(records, (r) => r.redisPool.commandTimeoutMs),
  );
  push(
    "redisPool.maxRetries",
    index(records, (r) => r.redisPool.maxRetries),
  );

  // Model set (union comparison)
  const allModels = new Set<string>();
  for (const r of records) for (const m of r.models) allModels.add(m);
  for (const m of allModels) {
    const values: Record<string, unknown> = {};
    for (const r of records) {
      values[r.id] = r.models.includes(m);
    }
    if (new Set(Object.values(values)).size > 1) {
      out.push({
        path: `models.${m}`,
        values,
        rationale: "Model availability may be gated by keys, account tier, or local config.",
      });
    }
  }

  // Enabled features (same shape)
  const allFeatures = new Set<string>();
  for (const r of records) for (const f of r.enabledFeatures) allFeatures.add(f);
  for (const f of allFeatures) {
    const values: Record<string, unknown> = {};
    for (const r of records) {
      values[r.id] = r.enabledFeatures.includes(f);
    }
    if (new Set(Object.values(values)).size > 1) {
      out.push({
        path: `features.${f}`,
        values,
        rationale: "Feature flags differ when each instance opted into a different surface area.",
      });
    }
  }

  return out;
}

function index<T>(
  records: InstanceRecord[],
  field: keyof InstanceRecord | ((r: InstanceRecord) => T),
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const r of records) {
    out[r.id] = typeof field === "function" ? field(r) : r[field];
  }
  return out;
}
